import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useNavigate } from "react-router-dom";
import { Loader2, LogOut } from "lucide-react";
import { api, okSchema, clearCsrfToken } from "@/lib/api";

/**
 * Sign-out control.
 *
 * The logout endpoints are POST (they mutate the session and are CSRF
 * protected), so this must not be an anchor — a plain link would issue a GET
 * and silently do nothing.
 */
export function LogoutButton({ as }: { as: "admin" | "patient" }) {
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const logout = useMutation({
    mutationFn: () => api.post(`/api/auth/${as}/logout`, okSchema),
    onSettled: () => {
      // Clear locally even if the request failed: the user asked to leave, and
      // any cached patient data must not survive on a shared device.
      clearCsrfToken();
      queryClient.clear();
      navigate(as === "admin" ? "/login" : "/patient/login", { replace: true });
    },
  });

  return (
    <button
      type="button"
      onClick={() => logout.mutate()}
      disabled={logout.isPending}
      aria-label="Keluar"
      className="text-void-400 hover:bg-void-800 hover:text-void-50 grid size-8 shrink-0 place-items-center rounded-lg disabled:opacity-50"
    >
      {logout.isPending ? (
        <Loader2 className="size-4 animate-spin" />
      ) : (
        <LogOut className="size-4" />
      )}
    </button>
  );
}
